import { useMemo } from 'react'
import { initialsFor } from '@/logic/labels'
import { useAuth } from '@/store/auth'
import { useHousehold } from '@/store/household'

/** person-1 is the household's owner, person-2 the other member. Names come from data. */
export type PersonTone = 'p1' | 'p2'

export interface People {
  meUid: string
  /** The other member, once they have joined. */
  other: { uid: string; name: string } | null
  names: Record<string, string>
  initials: Record<string, string>
  toneOf: (uid: string) => PersonTone
  /** Both members in a stable order: the owner first. */
  pair: string[]
}

/** Who is in the household, their initials, and which person colour each one has. */
export function usePeople(): People {
  const meUid = useAuth((s) => s.user?.uid ?? '')
  const household = useHousehold((s) => s.household)
  return useMemo(() => {
    const names: Record<string, string> = {}
    for (const [uid, m] of Object.entries(household?.members ?? {})) names[uid] = m.name
    const owner = household?.ownerUid ?? meUid
    const pair = Object.keys(names).sort((a, b) => (a === owner ? -1 : b === owner ? 1 : a.localeCompare(b)))
    const otherUid = pair.find((u) => u !== meUid) ?? null
    return {
      meUid,
      other: otherUid ? { uid: otherUid, name: names[otherUid] } : null,
      names,
      initials: initialsFor(names),
      toneOf: (uid: string) => (uid === owner ? 'p1' : 'p2'),
      pair,
    }
  }, [household, meUid])
}
